import {
	Accordion,
	type AccordionItemData,
	Card,
	Icon,
	IconTile,
	SectionCard,
	SunkenPanel,
} from "@iec-id/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { SupportButton } from "@/components/support/support-button";
import { useStickyColumn } from "@/lib/sticky-column";

export const Route = createFileRoute("/faq")({
	component: FaqSection,
});

const FAQ_ITEM_KEYS = [
	"what",
	"signin",
	"points",
	"plus",
	"services",
	"consents",
	"age",
	"support",
] as const;

/**
 * Адрес поддержки. Та же константа, что в `personal/identifier-panel.tsx` —
 * общего реестра контактов в `@iec-id/shared` пока нет; появится — переезжают
 * оба.
 */
const SUPPORT_EMAIL = "support@iec.exchange";

/**
 * Справка витрины: ответы про IEC ID, а не внешняя страница портала.
 * В левом меню раздела нет — сюда ведёт кнопка помощи в шапке.
 *
 * Вопросы свёрнуты: список из восьми развёрнутых ответов читался сплошным
 * полотном, в котором свой вопрос надо было искать глазами. Открыт один за раз
 * — здесь нет сценария «сравнить два ответа рядом».
 */
function FaqSection() {
	const { t } = useTranslation("faq");
	const asideRef = useStickyColumn<HTMLElement>();

	const items: AccordionItemData[] = FAQ_ITEM_KEYS.map((key) => ({
		value: key,
		question: t(`items.${key}.q`),
		answer: t(`items.${key}.a`),
	}));

	return (
		<div className="flex w-full min-w-0 flex-col gap-1 lg:flex-row lg:items-start">
			<div className="flex min-w-0 flex-1 flex-col gap-1">
				<SectionCard title={t("title")} level="page">
					<p className="t-body text-fg-secondary">{t("intro")}</p>
					<Accordion items={items} />
				</SectionCard>
			</div>

			{/* Контакты — рядом со списком, а не под ним: человек, не нашедший свой
			    вопрос, не должен для этого доскроллить до конца. */}
			<aside
				ref={asideRef}
				className="flex w-full flex-col gap-1 lg:sticky lg:w-[300px] lg:shrink-0"
			>
				<Card padding="mobile" gap="row" className="lg:p-5">
					<h2 className="t-subsection">{t("contact.title")}</h2>
					<p className="t-body text-fg-secondary">{t("contact.description")}</p>

					<a href={`mailto:${SUPPORT_EMAIL}`} className="text-fg no-underline hover:text-fg">
						<SunkenPanel
							padding="row"
							className="gap-3 transition-colors duration-150 ease-smooth hover:bg-btn-secondary-hover"
						>
							<IconTile tone="card">
								<Icon name="Mail" />
							</IconTile>
							<span className="flex min-w-0 flex-col gap-0.5">
								<span className="t-meta text-fg-muted">{t("contact.emailLabel")}</span>
								<span className="t-row break-all">{SUPPORT_EMAIL}</span>
							</span>
						</SunkenPanel>
					</a>

					<SupportButton />

					<Link to="/legal" className="t-body">
						{t("contact.legal")}
					</Link>
				</Card>
			</aside>
		</div>
	);
}
